import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileSpreadsheet } from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/components/card';
import { Badge } from '@/components/badge';
import { EmptyState } from '@/components/empty-state';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { api } from '@/lib/api';
import { dollars } from '@/lib/format';
import { cn } from '@/lib/cn';

type TrialBalanceRow = {
  id: number;
  code: string;
  name: string;
  type: string;
  schedule_c_line: string | null;
  debit_cents: number;
  credit_cents: number;
  balance_cents: number;
};

type TrialBalanceResponse = {
  from: string;
  to: string;
  rows: TrialBalanceRow[];
  total_debit_cents: number;
  total_credit_cents: number;
  is_balanced: boolean;
};

type ScheduleCAccount = {
  id: number;
  code: string;
  name: string;
  amount_cents: number;
};

type ScheduleCLine = {
  schedule_c_line: string;
  label: string;
  is_income: boolean;
  total_cents: number;
  accounts: ScheduleCAccount[];
};

type ScheduleCResponse = {
  from: string;
  to: string;
  lines: ScheduleCLine[];
  total_income_cents: number;
  total_expense_cents: number;
  net_income_cents: number;
};

type IncomeStatementAccount = { id: number; code: string; name: string; amount_cents: number };
type IncomeStatementSection = { total_cents: number; accounts: IncomeStatementAccount[] };
type IncomeStatementResponse = {
  from: string;
  to: string;
  basis: 'cash';
  revenue: IncomeStatementSection;
  cogs: IncomeStatementSection;
  gross_profit_cents: number;
  operating_expenses: IncomeStatementSection;
  operating_income_cents: number;
  other_income: IncomeStatementSection;
  net_income_cents: number;
};

type BalanceSheetAccount = { id: number; code: string; name: string; balance_cents: number };
type BalanceSheetResponse = {
  as_of: string;
  basis: 'cash';
  assets: { total_cents: number; accounts: BalanceSheetAccount[] };
  liabilities: { total_cents: number; accounts: BalanceSheetAccount[] };
  equity: {
    total_cents: number;
    current_earnings_cents: number;
    accounts: BalanceSheetAccount[];
  };
  total_liabilities_and_equity_cents: number;
  is_balanced: boolean;
};

type RevenuePeriod = { key: string; label: string; total_cents: number };
type RevenueAccount = { code: string; name: string; amount_cents: number };
type RevenueResponse = {
  group: 'month' | 'year';
  year: number;
  periods: RevenuePeriod[];
  accounts: RevenueAccount[];
  total_cents: number;
  max_period_cents: number;
  available_years: number[];
};

/** Shared From/To date range used by the period-scoped reports. */
function usePeriod() {
  const year = new Date().getFullYear();
  const [from, setFrom] = useState(`${year}-01-01`);
  const [to, setTo] = useState(`${year}-12-31`);
  return { from, to, setFrom, setTo };
}

function PeriodPicker({
  from,
  to,
  onFrom,
  onTo,
}: {
  from: string;
  to: string;
  onFrom: (v: string) => void;
  onTo: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="block space-y-1 text-sm">
        <span className="font-medium text-[var(--color-foreground-muted)]">From</span>
        <Input type="date" value={from} onChange={(e) => onFrom(e.target.value)} />
      </label>
      <label className="block space-y-1 text-sm">
        <span className="font-medium text-[var(--color-foreground-muted)]">To</span>
        <Input type="date" value={to} onChange={(e) => onTo(e.target.value)} />
      </label>
    </div>
  );
}

function RevenueChart({ data }: { data: RevenueResponse }) {
  const max = Math.max(1, data.max_period_cents);

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        {data.periods.map((p) => (
          <div key={p.key} className="flex items-center gap-3 text-sm">
            <span className="w-12 shrink-0 text-xs text-[var(--color-foreground-muted)]">
              {p.label}
            </span>
            <div className="relative h-5 flex-1 overflow-hidden rounded bg-[var(--color-surface-muted)]">
              <div
                className="h-full rounded bg-[color-mix(in_oklab,var(--color-primary)_70%,transparent)]"
                style={{ width: `${Math.max(0, (p.total_cents / max) * 100)}%` }}
              />
            </div>
            <span className="w-24 shrink-0 text-right tabular-nums">{dollars(p.total_cents)}</span>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between border-t border-[var(--color-border)] pt-2 text-sm font-semibold">
        <span>Total</span>
        <span className="tabular-nums">{dollars(data.total_cents)}</span>
      </div>

      {data.accounts.length > 1 ? (
        <div className="space-y-1.5 border-t border-[var(--color-border)] pt-3">
          <div className="text-xs font-medium uppercase tracking-wide text-[var(--color-foreground-subtle)]">
            By account
          </div>
          {data.accounts.map((a) => (
            <div key={a.code} className="flex items-center justify-between text-sm">
              <span className="text-[var(--color-foreground-muted)]">
                {a.code} · {a.name}
              </span>
              <span className="tabular-nums">{dollars(a.amount_cents)}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function RevenueReport() {
  const [revGroup, setRevGroup] = useState<'month' | 'year'>('month');
  const [revYear, setRevYear] = useState(new Date().getFullYear());

  const revenue = useQuery({
    queryKey: ['accounting', 'reports', 'revenue', { revGroup, revYear }],
    queryFn: async (): Promise<RevenueResponse> => {
      const { data } = await api.get<RevenueResponse>('/v1/accounting/reports/revenue', {
        params: { group: revGroup, year: revYear },
      });
      return data;
    },
  });

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Revenue</h3>
          <p className="text-xs text-[var(--color-foreground-muted)]">
            Income posted to your ledger, {revGroup === 'month' ? `by month for ${revYear}` : 'by year'}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div
            role="radiogroup"
            aria-label="Group revenue by"
            className="inline-flex rounded-md border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-0.5"
          >
            {(['month', 'year'] as const).map((g) => (
              <button
                key={g}
                type="button"
                role="radio"
                aria-checked={revGroup === g}
                onClick={() => setRevGroup(g)}
                className={cn(
                  'rounded px-2.5 py-1 text-xs font-medium capitalize transition-colors',
                  revGroup === g
                    ? 'bg-[var(--color-surface)] text-[var(--color-foreground)] shadow-sm'
                    : 'text-[var(--color-foreground-muted)] hover:text-[var(--color-foreground)]',
                )}
              >
                {g}
              </button>
            ))}
          </div>
          {revGroup === 'month' ? (
            <Select
              value={revYear}
              onChange={(e) => setRevYear(Number(e.target.value))}
              className="w-auto"
            >
              {(revenue.data?.available_years ?? [revYear]).map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Select>
          ) : null}
        </div>
      </CardHeader>
      <CardBody>
        {revenue.isLoading ? (
          <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>
        ) : !revenue.data || revenue.data.total_cents === 0 ? (
          <EmptyState
            icon={FileSpreadsheet}
            title="No revenue in this period"
            description="Once income is posted to your ledger, the trend appears here."
          />
        ) : (
          <RevenueChart data={revenue.data} />
        )}
      </CardBody>
    </Card>
  );
}

/** A titled group of accounts with a subtotal; totals for costs render parenthesized. */
function StatementSection({
  title,
  section,
  subtract,
}: {
  title: string;
  section: IncomeStatementSection;
  subtract?: boolean;
}) {
  if (section.accounts.length === 0) {
    return null;
  }
  return (
    <div>
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[var(--color-foreground-subtle)]">
        {title}
      </div>
      <ul>
        {section.accounts.map((a) => (
          <li
            key={a.id}
            className="flex items-center justify-between gap-2 px-2 py-1 text-sm"
          >
            <span className="truncate text-[var(--color-foreground-muted)]">
              <span className="font-mono text-xs">{a.code}</span> · {a.name}
            </span>
            <span className="tabular-nums">{dollars(a.amount_cents)}</span>
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-between border-t border-[var(--color-border)] px-2 py-1 text-sm font-medium">
        <span>Total {title.toLowerCase()}</span>
        <span className="tabular-nums">
          {subtract ? `(${dollars(section.total_cents)})` : dollars(section.total_cents)}
        </span>
      </div>
    </div>
  );
}

function SubtotalRow({
  label,
  cents,
  emphasis,
}: {
  label: string;
  cents: number;
  emphasis?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between border-y border-[var(--color-border)] px-2',
        emphasis ? 'bg-[var(--color-muted)] py-2 text-base font-semibold' : 'py-1.5 text-sm font-semibold',
      )}
    >
      <span>{label}</span>
      <span className="tabular-nums">{dollars(cents)}</span>
    </div>
  );
}

export function IncomeStatementReport() {
  const { from, to, setFrom, setTo } = usePeriod();

  const is = useQuery({
    queryKey: ['accounting', 'reports', 'income-statement', { from, to }],
    queryFn: async (): Promise<IncomeStatementResponse> => {
      const { data } = await api.get<IncomeStatementResponse>(
        '/v1/accounting/reports/income-statement',
        { params: { from, to } },
      );
      return data;
    },
  });

  const hasActivity =
    is.data != null &&
    (is.data.revenue.accounts.length > 0 ||
      is.data.cogs.accounts.length > 0 ||
      is.data.operating_expenses.accounts.length > 0 ||
      is.data.other_income.accounts.length > 0);

  return (
    <div className="space-y-5">
      <PeriodPicker from={from} to={to} onFrom={setFrom} onTo={setTo} />

      <Card>
        <CardHeader className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold">Income statement</h3>
            <p className="text-xs text-[var(--color-foreground-muted)]">
              Profit &amp; loss for the selected period.
            </p>
          </div>
          <Badge tone="neutral">Cash basis</Badge>
        </CardHeader>
        <CardBody>
          {is.isLoading ? (
            <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>
          ) : !hasActivity ? (
            <EmptyState
              icon={FileSpreadsheet}
              title="No activity in this period"
              description="Once income and expenses post to your ledger, the statement appears here."
            />
          ) : (
            <div className="space-y-4">
              <StatementSection title="Revenue" section={is.data!.revenue} />
              <StatementSection title="Cost of goods sold" section={is.data!.cogs} subtract />
              <SubtotalRow label="Gross profit" cents={is.data!.gross_profit_cents} />
              <StatementSection
                title="Operating expenses"
                section={is.data!.operating_expenses}
                subtract
              />
              <SubtotalRow label="Operating income" cents={is.data!.operating_income_cents} />
              <StatementSection title="Other income" section={is.data!.other_income} />
              <SubtotalRow label="Net income" cents={is.data!.net_income_cents} emphasis />
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

/** A titled group of balance-sheet accounts with a subtotal row. */
function BalanceGroup({
  title,
  accounts,
  total,
  extraRow,
}: {
  title: string;
  accounts: BalanceSheetAccount[];
  total: number;
  extraRow?: { label: string; cents: number };
}) {
  return (
    <div>
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[var(--color-foreground-subtle)]">
        {title}
      </div>
      <ul>
        {accounts.map((a) => (
          <li key={a.id} className="flex items-center justify-between gap-2 px-2 py-1 text-sm">
            <span className="truncate text-[var(--color-foreground-muted)]">
              <span className="font-mono text-xs">{a.code}</span> · {a.name}
            </span>
            <span className="tabular-nums">{dollars(a.balance_cents)}</span>
          </li>
        ))}
        {extraRow ? (
          <li className="flex items-center justify-between gap-2 px-2 py-1 text-sm">
            <span className="italic text-[var(--color-foreground-muted)]">{extraRow.label}</span>
            <span className="tabular-nums">{dollars(extraRow.cents)}</span>
          </li>
        ) : null}
      </ul>
      <div className="flex items-center justify-between border-t border-[var(--color-border)] px-2 py-1 text-sm font-semibold">
        <span>Total {title.toLowerCase()}</span>
        <span className="tabular-nums">{dollars(total)}</span>
      </div>
    </div>
  );
}

export function BalanceSheetReport() {
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));

  const bs = useQuery({
    queryKey: ['accounting', 'reports', 'balance-sheet', { asOf }],
    queryFn: async (): Promise<BalanceSheetResponse> => {
      const { data } = await api.get<BalanceSheetResponse>(
        '/v1/accounting/reports/balance-sheet',
        { params: { as_of: asOf } },
      );
      return data;
    },
  });

  const hasActivity =
    bs.data != null &&
    (bs.data.assets.accounts.length > 0 ||
      bs.data.liabilities.accounts.length > 0 ||
      bs.data.equity.accounts.length > 0 ||
      bs.data.equity.current_earnings_cents !== 0);

  return (
    <div className="space-y-5">
      <label className="block space-y-1 text-sm">
        <span className="font-medium text-[var(--color-foreground-muted)]">As of</span>
        <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
      </label>

      <Card>
        <CardHeader className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold">Balance sheet</h3>
            <p className="text-xs text-[var(--color-foreground-muted)]">
              Assets, liabilities, and equity as of the selected date.
            </p>
          </div>
          <Badge tone="neutral">Cash basis</Badge>
        </CardHeader>
        <CardBody>
          {bs.isLoading ? (
            <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>
          ) : !hasActivity ? (
            <EmptyState
              icon={FileSpreadsheet}
              title="Nothing posted yet"
              description="Once entries post to your ledger, the balance sheet appears here."
            />
          ) : (
            <div className="space-y-4">
              <BalanceGroup
                title="Assets"
                accounts={bs.data!.assets.accounts}
                total={bs.data!.assets.total_cents}
              />
              <BalanceGroup
                title="Liabilities"
                accounts={bs.data!.liabilities.accounts}
                total={bs.data!.liabilities.total_cents}
              />
              <BalanceGroup
                title="Equity"
                accounts={bs.data!.equity.accounts}
                total={bs.data!.equity.total_cents}
                extraRow={{
                  label: 'Current earnings',
                  cents: bs.data!.equity.current_earnings_cents,
                }}
              />
              <div className="flex items-center justify-between border-y border-[var(--color-border)] bg-[var(--color-muted)] px-2 py-2 text-base font-semibold">
                <span>Liabilities + equity</span>
                <div className="flex items-center gap-2">
                  <span className="tabular-nums">
                    {dollars(bs.data!.total_liabilities_and_equity_cents)}
                  </span>
                  <Badge tone={bs.data!.is_balanced ? 'success' : 'danger'}>
                    {bs.data!.is_balanced ? 'Balanced' : 'Out of balance'}
                  </Badge>
                </div>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

export function ScheduleCReport() {
  const { from, to, setFrom, setTo } = usePeriod();

  const sc = useQuery({
    queryKey: ['accounting', 'reports', 'schedule-c', { from, to }],
    queryFn: async (): Promise<ScheduleCResponse> => {
      const { data } = await api.get<ScheduleCResponse>('/v1/accounting/reports/schedule-c', {
        params: { from, to },
      });
      return data;
    },
  });

  return (
    <div className="space-y-5">
      <PeriodPicker from={from} to={to} onFrom={setFrom} onTo={setTo} />

      <Card>
        <CardHeader className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">Schedule C summary</h3>
            <p className="text-xs text-[var(--color-foreground-muted)]">
              Year-end view aligned to IRS Form 1040, Schedule C.
            </p>
          </div>
          {sc.data ? (
            <div className="text-right text-xs tabular-nums text-[var(--color-foreground-muted)]">
              <div>Income: {dollars(sc.data.total_income_cents)}</div>
              <div>Expenses: {dollars(sc.data.total_expense_cents)}</div>
              <div className="mt-0.5 font-semibold text-foreground">
                Net: {dollars(sc.data.net_income_cents)}
              </div>
            </div>
          ) : null}
        </CardHeader>
        <CardBody className="space-y-3">
          {sc.isLoading ? (
            <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>
          ) : !sc.data || sc.data.lines.length === 0 ? (
            <EmptyState
              icon={FileSpreadsheet}
              title="No postings in this period"
              description="Post journal entries (or let Stripe payments auto-post) and the lines will appear here."
            />
          ) : (
            sc.data.lines.map((line) => (
              <ScheduleCLineCard key={line.schedule_c_line} line={line} />
            ))
          )}
        </CardBody>
      </Card>
    </div>
  );
}

export function TrialBalanceReport() {
  const { from, to, setFrom, setTo } = usePeriod();

  const tb = useQuery({
    queryKey: ['accounting', 'reports', 'trial-balance', { from, to }],
    queryFn: async (): Promise<TrialBalanceResponse> => {
      const { data } = await api.get<TrialBalanceResponse>(
        '/v1/accounting/reports/trial-balance',
        { params: { from, to } },
      );
      return data;
    },
  });

  return (
    <div className="space-y-5">
      <PeriodPicker from={from} to={to} onFrom={setFrom} onTo={setTo} />

      <Card>
        <CardHeader>
          <h3 className="text-sm font-semibold">Trial balance</h3>
          <p className="text-xs text-[var(--color-foreground-muted)]">
            Movement per account in the period. Totals must agree.
          </p>
        </CardHeader>
        <CardBody>
          {tb.isLoading ? (
            <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>
          ) : !tb.data ? (
            <p className="text-sm text-[var(--color-foreground-muted)]">No data.</p>
          ) : (
            <div className="overflow-hidden rounded-md border border-[var(--color-border)]">
              <div className="grid grid-cols-[80px_1fr_120px_120px_120px] gap-2 border-b border-[var(--color-border)] bg-[var(--color-muted)] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-foreground-subtle)]">
                <div>Code</div>
                <div>Name</div>
                <div className="text-right">Debit</div>
                <div className="text-right">Credit</div>
                <div className="text-right">Balance</div>
              </div>
              {tb.data.rows
                .filter((r) => r.debit_cents !== 0 || r.credit_cents !== 0)
                .map((row) => (
                  <div
                    key={row.id}
                    className="grid grid-cols-[80px_1fr_120px_120px_120px] items-center gap-2 border-b border-[var(--color-border)] px-3 py-1.5 text-sm last:border-b-0"
                  >
                    <span className="font-mono text-xs text-muted-foreground">{row.code}</span>
                    <span className="truncate">{row.name}</span>
                    <span className="text-right tabular-nums">
                      {row.debit_cents ? dollars(row.debit_cents) : '—'}
                    </span>
                    <span className="text-right tabular-nums">
                      {row.credit_cents ? dollars(row.credit_cents) : '—'}
                    </span>
                    <span className="text-right font-medium tabular-nums">
                      {dollars(row.balance_cents)}
                    </span>
                  </div>
                ))}
              <div className="grid grid-cols-[80px_1fr_120px_120px_120px] items-center gap-2 bg-[var(--color-muted)] px-3 py-2 text-sm font-semibold">
                <span />
                <span>Totals</span>
                <span className="text-right tabular-nums">{dollars(tb.data.total_debit_cents)}</span>
                <span className="text-right tabular-nums">{dollars(tb.data.total_credit_cents)}</span>
                <span className="text-right">
                  <Badge tone={tb.data.is_balanced ? 'success' : 'danger'}>
                    {tb.data.is_balanced ? 'Balanced' : 'Out of balance'}
                  </Badge>
                </span>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function ScheduleCLineCard({ line }: { line: ScheduleCLine }) {
  return (
    <div className="rounded-md border border-[var(--color-border)] bg-card">
      <div className="flex items-center justify-between gap-2 border-b border-[var(--color-border)] px-3 py-2">
        <div className="flex items-center gap-2">
          <Badge tone={line.is_income ? 'success' : 'neutral'}>
            Line {line.schedule_c_line}
          </Badge>
          <span className="text-sm font-medium">{line.label}</span>
        </div>
        <span
          className={cn(
            'font-semibold tabular-nums',
            line.is_income ? 'text-[var(--success)]' : 'text-foreground',
          )}
        >
          {dollars(line.total_cents)}
        </span>
      </div>
      <ul className="divide-y divide-[var(--color-border)]">
        {line.accounts.map((a) => (
          <li
            key={a.id}
            className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs"
          >
            <span className="font-mono text-muted-foreground">{a.code}</span>
            <span className="flex-1 truncate text-muted-foreground">{a.name}</span>
            <span className="tabular-nums">{dollars(a.amount_cents)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
